import { MIN_TVL } from '@/config/query/allVaults'
import { DEFAULT_SETTINGS } from '@/context/settings'
import { parseFormattedNumber } from '@/utils/parsers/numbers'
import { formatAmount } from '@vaultsfyi/common/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '../common/input'
import { Separator } from '../common/separator'
import { Slider } from '../common/slider'

interface RangeSliderInputProps {
  initialValue: [number, number]
  onChange: (value: [number, number]) => void
}

export const TvlRangeSelector = ({ initialValue, onChange }: RangeSliderInputProps) => {
  const constrainedInitialValue: [number, number] = [
    Math.max(MIN_TVL, initialValue[0]),
    Math.max(MIN_TVL, initialValue[1]),
  ]

  const [displayValue, setDisplayValue] = useState(constrainedInitialValue)
  const [sliderRange, setSliderRange] = useState(constrainedInitialValue.map(tvlToSliderScale))
  const [inputRange, setInputRange] = useState<[string, string]>(
    constrainedInitialValue.map((num) => formatAmount(num, { notation: 'compact' })) as [string, string]
  )
  const [minFocused, setMinFocused] = useState(false)
  const [maxFocused, setMaxFocused] = useState(false)
  const minInputRef = useRef<HTMLInputElement>(null)
  const maxInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const constrainedValue: [number, number] = [Math.max(MIN_TVL, initialValue[0]), Math.max(MIN_TVL, initialValue[1])]
    setDisplayValue(constrainedValue)
    setSliderRange(constrainedValue.map(tvlToSliderScale))
    setInputRange(constrainedValue.map((num) => formatAmount(num, { notation: 'compact' })) as [string, string])
  }, [initialValue])

  const handleInputRangeChange = useCallback(() => {
    let lowerBound = parseFormattedNumber(inputRange[0]) ?? displayValue[0]
    let upperBound = parseFormattedNumber(inputRange[1]) ?? displayValue[1]

    lowerBound = Math.max(MIN_TVL, lowerBound)
    upperBound = Math.max(MIN_TVL, upperBound)

    if (lowerBound > upperBound) {
      lowerBound = upperBound
    }

    const newValue: [number, number] = [lowerBound, upperBound]
    setDisplayValue(newValue)
    setSliderRange(newValue.map(tvlToSliderScale))
    onChange(newValue)
  }, [inputRange, displayValue, onChange])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (minFocused || maxFocused) {
        if (e.key === 'Enter' || e.key === 'Escape') {
          handleInputRangeChange()
          if (minFocused && minInputRef.current) {
            minInputRef.current.blur()
          }
          if (maxFocused && maxInputRef.current) {
            maxInputRef.current.blur()
          }
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [minFocused, maxFocused, handleInputRangeChange])

  return (
    <div className="flex flex-col gap-1">
      <Slider
        min={SLIDER_MIN_VALUE}
        max={SLIDER_MAX_VALUE}
        value={sliderRange}
        onValueChange={(value) => {
          const tvlValues = value.map(sliderScaleToTvl) as [number, number]
          const constrainedValues: [number, number] = [Math.max(MIN_TVL, tvlValues[0]), Math.max(MIN_TVL, tvlValues[1])]
          setDisplayValue(constrainedValues)
          setSliderRange(constrainedValues.map(tvlToSliderScale))
          onChange(constrainedValues)
        }}
      />
      <div className="grid grid-cols-[1fr_0.5rem_1fr] items-center gap-1">
        <Input
          ref={minInputRef}
          value={minFocused ? inputRange[0] : formatAmount(displayValue[0], { notation: 'compact' })}
          onFocus={() => setMinFocused(true)}
          onBlur={() => {
            setMinFocused(false)
            handleInputRangeChange()
          }}
          onChange={(e) => setInputRange([e.target.value, inputRange[1]])}
          placeholder="Min (100k)"
          inputMode="numeric"
        />
        <Separator />
        <Input
          ref={maxInputRef}
          value={maxFocused ? inputRange[1] : formatAmount(displayValue[1], { notation: 'compact' })}
          onFocus={() => setMaxFocused(true)}
          onBlur={() => {
            setMaxFocused(false)
            handleInputRangeChange()
          }}
          onChange={(e) => setInputRange([inputRange[0], e.target.value])}
          placeholder="Max"
          inputMode="numeric"
        />
      </div>
    </div>
  )
}

const SLIDER_MIN_VALUE = 0
const SLIDER_MAX_VALUE = 1000

const [TVL_LOG_MIN_VALUE, TVL_LOG_MAX_VALUE] = [Math.log10(MIN_TVL), Math.log10(DEFAULT_SETTINGS.tvlRange[1])]

function tvlToSliderScale(tvl: number): number {
  const constrainedTvl = Math.max(MIN_TVL, tvl)
  const normalizedToLogScale = normalize(Math.log10(constrainedTvl), TVL_LOG_MIN_VALUE, TVL_LOG_MAX_VALUE)
  return denormalize(normalizedToLogScale, SLIDER_MIN_VALUE, SLIDER_MAX_VALUE)
}

function sliderScaleToTvl(sliderValue: number): number {
  const normalizedToLinearScale = normalize(sliderValue, SLIDER_MIN_VALUE, SLIDER_MAX_VALUE)
  const tvl = Math.round(10 ** denormalize(normalizedToLinearScale, TVL_LOG_MIN_VALUE, TVL_LOG_MAX_VALUE))
  return Math.max(MIN_TVL, tvl)
}

function normalize(value: number, min: number, max: number): number {
  return (value - min) / (max - min)
}

function denormalize(value: number, min: number, max: number): number {
  return value * (max - min) + min
}
